import {notifications} from '@mantine/notifications'

import {datesForTbcJob} from '../dispatch-timeline/date-tbc'
import type {DispatchTimelineData} from '../dispatch-timeline/types'
import {showDateMoveWarnings} from '../jobs/date-move-warnings'
import {usePatchJob, useReportJobDidNotHappen} from '../jobs/use-jobs'
import {alertToneColor} from '../theme/semantic-colors'
import type {DispatchYard} from './schemas'
import {useSetProjectDispatchYard} from './use-dispatch'

type BoardJobChangesOptions = {
    canonicalData: DispatchTimelineData
    yards: DispatchYard[]
    refresh: () => Promise<void>
    closeJob: () => void
}

export function useBoardJobChanges({
    canonicalData,
    yards,
    refresh,
    closeJob,
}: BoardJobChangesOptions) {
    const patchJob = usePatchJob()
    const reportDidNotHappen = useReportJobDidNotHappen()
    const setProjectYard = useSetProjectDispatchYard()

    async function save<T>(
        run: () => Promise<T>,
        success: {title: string; message: string},
        failureTitle: string,
        {keepOpen = false, rethrow = false} = {},
    ): Promise<T | undefined> {
        try {
            const result = await run()
            if (!keepOpen) closeJob()
            await refresh()
            notifications.show({...success, color: alertToneColor('success')})
            return result
        } catch (error) {
            notifications.show({
                title: failureTitle,
                message: error instanceof Error ? error.message : 'Try again.',
                color: alertToneColor('danger'),
            })
            if (rethrow) throw error
            return undefined
        }
    }

    return {
        extendEndDate: async (jobId: string, endDate: string) => {
            const updated = await save(
                () => patchJob.mutateAsync({jobId, input: {endDate}}),
                {title: 'End date extended', message: `End date set to ${endDate}`},
                'Could not extend the end date',
            )
            if (updated) showDateMoveWarnings(updated.warnings)
        },
        setJobDate: async (jobId: string, date: string) => {
            const dayCount = canonicalData.jobIdentities?.[jobId]?.dateTbcDayCount
            const updated = await save(
                () => patchJob.mutateAsync({jobId, input: datesForTbcJob(date, dayCount)}),
                {title: 'Date set', message: `Scheduled for ${date}`},
                'Could not set the date',
            )
            if (updated) showDateMoveWarnings(updated.warnings)
        },
        setProjectYard: async (projectId: string, dispatchYardId: string) => {
            const yardName = yards.find((yard) => yard.id === dispatchYardId)?.name ?? 'that yard'
            await save(
                () => setProjectYard.mutateAsync({projectId, dispatchYardId}),
                {title: 'Yard set', message: `Jobs moved to ${yardName}`},
                'Could not set the yard',
            )
        },
        reportDidNotHappen: async (jobId: string, note: string) => {
            await save(
                () => reportDidNotHappen.mutateAsync({jobId, note}),
                {title: 'Reported as did not happen', message: 'Rep notified'},
                'Could not report that the job did not happen',
                {keepOpen: true, rethrow: true},
            )
        },
        markCompleted: async (jobId: string) => {
            await save(
                () => patchJob.mutateAsync({jobId, input: {status: 'completed'}}),
                {title: 'Job completed', message: 'Removed from the board'},
                'Could not complete the job',
            )
        },
    }
}
